import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Room } from '../types';
import { RoomService } from '../services/roomService';

export const useRealtimeBroadcast = (roomId: string, linkId: string) => {
  const [room, setRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const subscriptionRef = useRef<any>(null);

  // Load initial room data
  useEffect(() => {
    const loadRoom = async () => {
      if (!roomId || !linkId) {
        setError('Invalid link');
        setLoading(false);
        return;
      }

      try {
        const roomData = await RoomService.getRoomByBroadcastLink(roomId, linkId);
        if (roomData) {
          setRoom(roomData);
        } else {
          setError('Invalid or expired link');
        }
      } catch (err) {
        setError('Failed to load room');
      } finally {
        setLoading(false);
      }
    };

    loadRoom();
  }, [roomId, linkId]);

  // Set up real-time subscription for this specific room
  useEffect(() => {
    if (!roomId || !linkId) {
      return;
    }

    const setupRealtimeSubscription = () => {
      // Clean up existing subscription
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }

      // Subscribe to changes for this specific room
      subscriptionRef.current = supabase
        .channel(`broadcast-room-${roomId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'room_state_history',
            filter: `room_id=eq.${roomId}`
          },
          async (payload: any) => {
            console.log('Broadcast room state change:', payload);
            try {
              // Reload the room data to get the latest state
              const updatedRoom = await RoomService.getRoomByBroadcastLink(roomId, linkId);
              if (updatedRoom) {
                setRoom(updatedRoom);
                setError(null);
              } else {
                setError('Room no longer available');
              }
            } catch (error) {
              console.error('Failed to reload room after state change:', error);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'rooms',
            filter: `id=eq.${roomId}`
          },
          async (payload: any) => {
            console.log('Broadcast room update:', payload);
            try {
              // Reload the room data to get the latest settings
              const updatedRoom = await RoomService.getRoomByBroadcastLink(roomId, linkId);
              if (updatedRoom) {
                setRoom(updatedRoom);
                setError(null);
              } else {
                setError('Room no longer available');
              }
            } catch (error) {
              console.error('Failed to reload room after update:', error);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'broadcast_links',
            filter: `id=eq.${linkId}`
          },
          async (payload: any) => {
            console.log('Broadcast link update:', payload);
            // If the link was deactivated, show error
            if (payload.new && !payload.new.active) {
              setError('This broadcast link has been deactivated');
              setRoom(null);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'room_states',
            filter: `room_id=eq.${roomId}`
          },
          async (payload: any) => {
            console.log('Broadcast room states change:', payload);
            try {
              // Reload the room data to get the latest states
              const updatedRoom = await RoomService.getRoomByBroadcastLink(roomId, linkId);
              if (updatedRoom) {
                setRoom(updatedRoom);
                setError(null);
              }
            } catch (error) {
              console.error('Failed to reload room after states change:', error);
            }
          }
        )
        .subscribe((status: any) => {
          console.log('Broadcast realtime subscription status:', status);
        });
    };

    setupRealtimeSubscription();

    // Cleanup on unmount or room change
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [roomId, linkId]);

  return {
    room,
    loading,
    error
  };
};